import { useEffect, useId, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { CopyButton, EnvironmentTag, Icon } from '@pryme/ui';
import { downloadText, envFile } from '../lib/files';
import { isLocalUrl } from '../lib/portal';
import type { CredentialPanel } from '../lib/types';
import { CopyField } from './CodeBlock';

/** Marks a loopback endpoint so a local stack is never mistaken for the hosted sandbox. */
export function LocalTag(props: { url: string }) {
  if (!isLocalUrl(props.url)) return null;
  return (
    <span className="dv-local" title="This portal is connected to a Pryme stack running on this machine">
      Local
    </span>
  );
}

/** The visible prefix of a secret key: sk_test_ in sandbox, sk_live_ in production. */
export const secretPrefix = (environment: string) => (environment === 'production' ? 'sk_live_' : 'sk_test_');

/**
 * One-time credentials panel: a public service ID and one secret key, like any API credential.
 * The secret carries this generation's signing key, Pryme's verification key for it and the API
 * settings (ADR-0013); it authenticates the service channel only (ADR-0014); its contents are shown under "What the secret key contains" for those who
 * want them. Navigation away is blocked until the developer confirms they saved the secret.
 */
export function OneTimeSecretPanel(props: { panel: CredentialPanel; onAcknowledged(): void; doneLabel?: string }) {
  const p = props.panel;
  const ackId = useId();
  const [ack, setAck] = useState(false);
  const [reveal, setReveal] = useState(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => !ack && currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    if (ack) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [ack]);

  return (
    <section className="dv-once" aria-labelledby={`${ackId}-h`}>
      <div className="dv-once-head">
        <Icon name="key" size={18} />
        <h2 id={`${ackId}-h`}>Your credentials — shown once</h2>
        <span className="p-spacer" style={{ flex: 1 }} />
        <EnvironmentTag environment={p.environment} />
      </div>
      <div className="dv-once-body">
        <p className="p-small" style={{ marginBottom: '1.25rem' }}>
          <strong>{p.warning}</strong> Download the <code>.env</code> file or copy the secret key into your backend's secret manager now. If it is lost, rotate the
          credentials; if it may have been exposed, rotate and revoke.
          {p.revoked && p.revoked.length > 0 && <> Revoked previous keys: {p.revoked.map((k) => <code key={k} className="p-mono-chip" style={{ marginRight: 4 }}>{k}</code>)}.</>}
        </p>

        <CopyField label="Service ID" envVar="PRYME_SERVICE_ID" value={p.serviceId} hint="Public. Identifies your service, for example as client_id in the sign-in redirect." />

        <div className="dv-secret-block">
          <div className="p-row p-between" style={{ gap: '0.5rem' }}>
            <span className="p-label">Secret key</span>
            <span className="dv-envvar">PRYME_SECRET_KEY</span>
          </div>
          <div className="p-secret" aria-live="polite">{reveal ? p.secretKey : `${secretPrefix(p.environment)}${'•'.repeat(40)}`}</div>
          <div className="p-row" style={{ gap: '0.5rem' }}>
            <button type="button" className="p-btn small" onClick={() => downloadText(`pryme-${p.serviceId}.env`, envFile(p))}>
              Download .env
            </button>
            <button type="button" className="p-btn secondary small" onClick={() => setReveal((r) => !r)} aria-pressed={reveal}>
              {reveal ? 'Hide' : 'Reveal'}
            </button>
            <CopyButton value={p.secretKey} label="Copy secret" />
          </div>
          <p className="p-hint" style={{ margin: '0.6rem 0 0' }}>
            Backend only. Never put it in browser or mobile code, logs or chat. It authenticates your backend to Pryme and lets the SDK verify everything Pryme sends
            back. Pryme does not keep it.
          </p>
        </div>

        <div className="dv-cred" style={{ paddingBottom: '0.9rem' }}>
          <p className="p-small" style={{ margin: '0 0 0.5rem' }}>
            <strong>That is all your backend needs.</strong> The SDK reads the API base, audience and Pryme's verification keys from the secret key:
          </p>
          <pre className="p-code" tabIndex={0}>{`const pryme = createPryme({
  serviceId: process.env.PRYME_SERVICE_ID,
  secretKey: process.env.PRYME_SECRET_KEY,
});`}</pre>
        </div>

        <details className="dv-advanced">
          <summary>What the secret key contains</summary>
          <p className="p-small p-muted" style={{ margin: '0.75rem 0 0' }}>
            You don't need these values to integrate. They are listed for auditing, for other languages, and for configuring the SDK field by field.
          </p>
          <dl className="p-kv p-small" style={{ marginTop: '0.9rem' }}>
            <dt>Signing key</dt>
            <dd>
              Key ID <code className="p-mono-chip">{p.serviceSigningKey.keyId}</code> · fingerprint <code className="p-mono-chip">{p.serviceSigningKey.fingerprint}</code>
              <div className="p-muted">
                An Ed25519 secret that signs your backend's requests to Pryme (Service B key in the specification). Pryme holds only its public half:{' '}
                <code className="p-mono-chip">{p.serviceSigningKey.publicKey}</code>
              </div>
            </dd>
            <dt>Verification key</dt>
            <dd>
              Key ID <code className="p-mono-chip">{p.prymePublicKey.keyId}</code> · fingerprint <code className="p-mono-chip">{p.prymePublicKey.fingerprint}</code>
              <div className="p-muted">
                The Ed25519 public key Pryme signs with when it answers requests made with this secret key (Service A key). It travels inside the secret and is never
                fetched at runtime, so it is your pinned trust anchor. Each secret key has its own; rotating gives the new secret a new one.{' '}
                <code className="p-mono-chip">{p.prymePublicKey.publicKey}</code>
              </div>
            </dd>
            <dt>API</dt>
            <dd>
              <code className="p-mono-chip">{p.apiBase}</code> <LocalTag url={p.apiBase} /> · audience <code className="p-mono-chip">{p.apiAudience}</code>
              <div className="p-muted">The audience is part of every signature, so a request signed for one environment cannot be replayed in another.</div>
            </dd>
            <dt>Issuer</dt>
            <dd>
              <code className="p-mono-chip">{p.issuer}</code>
            </dd>
            <dt>Format</dt>
            <dd>
              <code>{secretPrefix(p.environment)}</code> followed by base64url JSON. Signatures are compact JWS with <code>alg: EdDSA</code> over JCS-canonical JSON (RFC 8785).
            </dd>
          </dl>
        </details>
      </div>

      <div className="dv-once-foot">
        <label className="p-check" htmlFor={ackId}>
          <input id={ackId} type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          <span><strong>I've saved these.</strong> I understand Pryme cannot show the secret key again.</span>
        </label>
        <button type="button" className="p-btn" disabled={!ack} onClick={props.onAcknowledged}>
          {props.doneLabel ?? 'Done'}
        </button>
      </div>

      {blocker.state === 'blocked' && (
        <div role="alertdialog" aria-modal="true" aria-label="Unsaved credentials" className="p-alert bad" style={{ margin: '0 1.5rem 1.25rem' }}>
          <p><strong>You haven't confirmed that the secret key is saved.</strong> If you leave now it is gone for good and you will have to rotate the credentials.</p>
          <div className="p-row">
            <button type="button" className="p-btn secondary small" onClick={() => blocker.reset()} autoFocus>Stay and save it</button>
            <button type="button" className="p-btn danger small" onClick={() => blocker.proceed()}>Leave anyway</button>
          </div>
        </div>
      )}
    </section>
  );
}
